import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, AlertTriangle } from 'lucide-react';

export default function EditRoomModal({ isOpen, onClose, room, onSave, loading }) {
  const [formData, setFormData] = useState({
    roomNumber: '',
    roomType: 'Shared',
    numBeds: 2,
    rentPerBed: 5000,
    floorId: ''
  });
  const [capacityWarning, setCapacityWarning] = useState('');

  useEffect(() => {
    if (room) {
      setFormData({
        roomNumber: room.roomNumber,
        roomType: room.roomType,
        numBeds: room.numBeds,
        rentPerBed: room.rentPerBed,
        floorId: room.floorId
      });
    }
  }, [room]);

  useEffect(() => {
    if (room && formData.numBeds < room.numBeds) {
      const bedsToRemove = room.numBeds - formData.numBeds;
      setCapacityWarning(
        `Reducing capacity will remove ${bedsToRemove} bed(s) (Bed ${formData.numBeds + 1} to Bed ${room.numBeds}). Ensure these beds are vacant.`
      );
    } else {
      setCapacityWarning('');
    }
  }, [formData.numBeds, room]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" data-testid="edit-room-modal">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900 font-heading">Edit Room</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="close-edit-room-modal"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Room Number *</Label>
            <Input
              placeholder="e.g., 101"
              value={formData.roomNumber}
              onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })}
              className="h-12 rounded-lg"
              required
              data-testid="edit-room-number"
            />
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Room Type *</Label>
            <select
              value={formData.roomType}
              onChange={(e) => setFormData({ ...formData, roomType: e.target.value })}
              className="h-12 w-full px-4 rounded-lg border border-slate-300 bg-white focus:ring-2 focus:ring-primary/20"
              data-testid="edit-room-type"
            >
              <option>Shared</option>
              <option>Private</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Number of Beds *</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={formData.numBeds}
                onChange={(e) => setFormData({ ...formData, numBeds: parseInt(e.target.value) })}
                className="h-12 rounded-lg"
                required
                data-testid="edit-room-beds"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Rent per Bed (₹) *</Label>
              <Input
                type="number"
                value={formData.rentPerBed}
                onChange={(e) => setFormData({ ...formData, rentPerBed: parseFloat(e.target.value) })}
                className="h-12 rounded-lg"
                required
                data-testid="edit-room-rent"
              />
            </div>
          </div>

          {capacityWarning && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-900">
                <span className="font-semibold">Warning:</span> {capacityWarning}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 h-12 rounded-xl"
              disabled={loading}
              data-testid="cancel-edit-room"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 rounded-xl bg-primary"
              disabled={loading}
              data-testid="save-edit-room"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
